import { Button } from "@/components/ui/button";
import { Video, LogOut, Menu, Clock } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { useApprovalStatus } from "@/hooks/useApprovalStatus";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

const Navbar = ({ compact = false }: { compact?: boolean }) => {
  const { user, signOut } = useAuth();
  const { isAuctioneer, isAdmin, isClerk, isBidder, isStaff } = useUserRole();
  const { isPending } = useApprovalStatus();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    setOpen(false);
  };

  const roles: string[] = [];
  if (isAdmin) roles.push("Admin");
  if (isAuctioneer) roles.push("Auctioneer");
  if (isClerk) roles.push("Clerk");
  if (isBidder) roles.push("Bidder");

  const initials = user?.email?.substring(0, 2).toUpperCase() ?? "U";

  const StaffLinks = () => (
    <>
      <Link to="/dashboard" onClick={() => setOpen(false)}>
        <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Dashboard</Button>
      </Link>
      <Link to="/schedule" onClick={() => setOpen(false)}>
        <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Schedule</Button>
      </Link>
      <Link to="/auction-posts" onClick={() => setOpen(false)}>
        <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Auction Posts</Button>
      </Link>
      <Link to="/chat-archives" onClick={() => setOpen(false)}>
        <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Chat Archives</Button>
      </Link>
      <Link to="/users" onClick={() => setOpen(false)}>
        <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Users</Button>
      </Link>
      {isAdmin && (
        <Link to="/settings" onClick={() => setOpen(false)}>
          <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Settings</Button>
        </Link>
      )}
    </>
  );

  const BidderLinks = () => (
    <Link to="/streams" onClick={() => setOpen(false)}>
      <Button variant="ghost" size="sm" className="w-full justify-start md:w-auto">Auctions</Button>
    </Link>
  );

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm ${compact ? "h-12" : ""}`}>
      <div className={`container mx-auto px-4 ${compact ? "h-12" : "h-16"} flex items-center gap-6`}>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group shrink-0">
          <div className={`${compact ? "w-8 h-8" : "w-9 h-9"} bg-gradient-primary rounded-lg flex items-center justify-center group-hover:shadow-glow-primary transition-smooth`}>
            <Video className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-primary-foreground`} />
          </div>
          <span className={`${compact ? "text-base" : "text-lg"} font-bold text-foreground hidden sm:inline`}>
            Bridge Street Auctions
          </span>
        </Link>

        {/* Desktop nav links — centered/left after logo */}
        {user && (
          <div className="hidden md:flex items-center gap-1 flex-1">
            {isStaff ? <StaffLinks /> : <BidderLinks />}
          </div>
        )}

        {/* Desktop right side — user menu or auth buttons */}
        <div className="hidden md:flex items-center gap-2 ml-auto shrink-0">
          {user ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="relative px-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-gradient-primary text-primary-foreground text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  {isPending && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-yellow-500 rounded-full border border-card" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" align="end">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Signed in as</p>
                    <p className="text-sm font-medium break-all">{user.email}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {isPending && (
                      <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-700 text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending Approval
                      </Badge>
                    )}
                    {roles.map((role) => (
                      <Badge key={role} variant="secondary" className="text-xs">{role}</Badge>
                    ))}
                  </div>
                  <div className="border-t border-border pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSignOut}
                      className="w-full justify-start text-muted-foreground hover:text-foreground"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/auth">
                <Button variant="default" size="sm">Sign Up</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="md:hidden ml-auto">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64">
              <div className="flex flex-col gap-4 mt-8">
                {user ? (
                  <>
                    {/* Mobile user info */}
                    <div className="pb-4 border-b border-border">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 relative shrink-0">
                          <AvatarFallback className="bg-gradient-primary text-primary-foreground font-semibold">
                            {initials}
                          </AvatarFallback>
                          {isPending && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-background" />
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.email}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {isPending && (
                              <Badge variant="secondary" className="text-xs bg-yellow-500/20 text-yellow-700">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                            {roles.map((role) => (
                              <Badge key={role} variant="secondary" className="text-xs">{role}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mobile nav links */}
                    <div className="flex flex-col gap-1">
                      {isStaff ? <StaffLinks /> : <BidderLinks />}
                    </div>

                    {/* Mobile sign out */}
                    <div className="border-t border-border pt-2 mt-auto">
                      <Button
                        variant="ghost"
                        onClick={handleSignOut}
                        className="w-full justify-start text-muted-foreground"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link to="/auth" onClick={() => setOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">Sign In</Button>
                    </Link>
                    <Link to="/auth" onClick={() => setOpen(false)}>
                      <Button variant="default" className="w-full">Sign Up</Button>
                    </Link>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

      </div>
    </nav>
  );
};

export default Navbar;
